/**
 * IndexedDB database schema and setup using Dexie
 * Stores hierarchical report data in flat format for efficient querying
 *
 * IMPORTANT: Schema is intentionally generic to support dynamic columns
 * Different pipelines will have different column sets - we only index
 * the common fields (pipeline_key, user_id) and store everything else as JSON
 */

import Dexie from 'dexie'

class ReportDatabase extends Dexie {
  constructor () {
    super('DDPReportDatabase')

    // Define schema - GENERIC for dynamic columns
    this.version(1).stores({
      // Flat row storage with only essential indexes
      // Only index fields that are common across ALL pipelines:
      //   - id: Auto-increment primary key
      //   - pipeline_key: Identifies which report (e.g., "bob_firm123")
      //   - user_id: User isolation/security
      //   - [pipeline_key+user_id]: Compound index for efficient queries
      //
      // All other columns (advisor_id, account_name, etc.) are stored as JSON
      // and queried via filtering (no indexes needed - filtering is still fast)
      rows: '++id, pipeline_key, user_id, [pipeline_key+user_id]',

      // Pre-built hierarchies for instant loading (no grouping needed)
      // Stores the complete tree structure after building once
      hierarchies: 'key, pipeline_key, user_id, [pipeline_key+user_id], updated_at',

      // Metadata for tracking report versions and freshness
      // status: 'loading' | 'building_hierarchy' | 'complete' | 'error'
      metadata: 'key, pipeline_code, user_id, updated_at, row_count, version, status'
    })

    // TypeScript-style table declarations (for IDE autocomplete)
    this.rows = this.table('rows')
    this.hierarchies = this.table('hierarchies')
    this.metadata = this.table('metadata')
  }
}

// Singleton instance
export const db = new ReportDatabase()

/**
 * Generate cache key for a report
 * Includes pipeline code and all variables for uniqueness
 * Works with any variable names (dynamic columns)
 */
export function getCacheKey (pipelineCode, variables) {
  // Sort variables for consistent key generation
  const sortedVars = Object.keys(variables || {})
    .sort()
    .map(key => `${key}:${variables[key]}`)
    .join('_')

  return sortedVars ? `${pipelineCode}_${sortedVars}` : pipelineCode
}

/**
 * Clear all data for a specific user
 * Call this on logout to prevent data leakage
 */
export async function clearUserData (userId) {
  await db.rows.where('user_id').equals(userId).delete()
  await db.hierarchies.where('user_id').equals(userId).delete()
  await db.metadata.where('user_id').equals(userId).delete()
}

/**
 * Clear all data from database
 * Nuclear option - use with caution
 */
export async function clearAllData () {
  await db.rows.clear()
  await db.hierarchies.clear()
  await db.metadata.clear()
}

/**
 * Get database statistics
 */
export async function getDatabaseStats () {
  const rowCount = await db.rows.count()
  const metadataCount = await db.metadata.count()

  return {
    totalRows: rowCount,
    totalReports: metadataCount,
    estimatedSize: rowCount * 1024 // Rough estimate: 1KB per row
  }
}

export default db
